import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../store';
import { fetchTenants, fetchDeletedTenants, deleteTenant } from '../store/slices/tenantSlice';
import { addNotification, showModal } from '../store/slices/uiSlice';
import { FiPlus, FiEdit2, FiTrash2, FiEye, FiSearch, FiCopy, FiArchive, FiDatabase, FiFilter, FiKey, FiRefreshCw } from 'react-icons/fi';
import { KcOverallStatus } from '../api/tenantApi';
import { testTenantProvider } from '../store/slices/tenantSlice';


const TenantsPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { tenants, loadingTenant, error } = useSelector((state: RootState) => state.tenants);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [showDeleted, setShowDeleted] = useState(false);

    const [showTestModal, setShowTestModal] = useState(false);
    const [testDomain, setTestDomain] = useState('');
    const [testApiKey, setTestApiKey] = useState('');
    const [testResult, setTestResult] = useState<{ success: boolean; data?: any; error?: string } | null>(null);
    const [useManualApiKey, setUseManualApiKey] = useState(false);


    useEffect(() => {
        if (showDeleted) {
            dispatch(fetchDeletedTenants());
        } else {
            dispatch(fetchTenants());
        }
    }, [dispatch, showDeleted]);

    // Add the test handler
    const handleTestProvider = async () => {
        if (!testDomain) {
            dispatch(
                addNotification({
                    type: 'error',
                    message: 'Please enter a domain to test',
                })
            );
            return;
        }

        try {
            setTestResult(null);

            // Use manual key if provided, otherwise let the backend use the environment key
            const apiKeyToUse = useManualApiKey && testApiKey ? testApiKey : undefined;

            const result = await dispatch(
                testTenantProvider({
                    tenantDomain: testDomain
                })
            ).unwrap();

            setTestResult({
                success: true,
                data: result,
            });

         /*   dispatch(
                addNotification({
                    type: 'success',
                    message: `Successfully found tenant: ${JSON.stringify(result)}`,
                })
            );*/
        } catch (error: any) {
            setTestResult({
                success: false,
                error: error || 'Failed to test tenant provider',
            });

            dispatch(
                addNotification({
                    type: 'error',
                    title: 'Test Failed',
                    message: error || 'Failed to test tenant provider',
                })
            );
        }
    };

    const handleDelete = (id: string, tenantId: string) => {
        dispatch(
            showModal({
                title: 'Confirm Deletion',
                content: `Are you sure you want to delete tenant "${tenantId}"? The tenant will be moved to the deleted tenants list and can be restored later.`,
                confirmLabel: 'Delete',
                cancelLabel: 'Cancel',
                onConfirm: async () => {
                    try {
                        await dispatch(deleteTenant(id)).unwrap();

                        dispatch(
                            addNotification({
                                type: 'success',
                                message: `Tenant "${tenantId}" was successfully deleted.`,
                            })
                        );
                    } catch (error: any) {
                        dispatch(
                            addNotification({
                                type: 'error',
                                title: 'Deletion Failed',
                                message: error.message || 'Failed to delete tenant.',
                            })
                        );
                    }
                },
            })
        );
    };
    console.log("testResult", testResult); 
    const handleDuplicate = (id: string) => {
        navigate(`/tenants/${id}/duplicate`);
    };

    // Filter tenants based on search term and status
    const filteredTenants = tenants.filter((tenant) => {
        const matchesSearch =
            tenant.tenantId.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (tenant.name && tenant.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (tenant.subdomain && tenant.subdomain.toLowerCase().includes(searchTerm.toLowerCase()));

        const matchesStatus = statusFilter === 'all' || tenant.status === statusFilter;

        return matchesSearch && matchesStatus;
    });

    // KC status dot — derived from the stored keycloak.sync.kcCheck.overall field (no live call)
    const kcStatusDot = (tenant: any) => {
        const overall: KcOverallStatus | undefined = tenant.keycloak?.sync?.kcCheck?.overall;
        const checkedAt: string | undefined        = tenant.keycloak?.sync?.kcCheck?.checkedAt;
        const age = checkedAt ? Math.round((Date.now() - new Date(checkedAt).getTime()) / 60000) : null;

        const configs: Record<string, { dot: string; label: string }> = {
            synced:       { dot: 'bg-green-500',  label: 'KC synced' },
            partial:      { dot: 'bg-yellow-500', label: 'KC partial' },
            missing:      { dot: 'bg-red-500',    label: 'KC realm missing' },
            unreachable:  { dot: 'bg-gray-400',   label: 'KC unreachable' },
            unconfigured: { dot: 'bg-gray-300',   label: 'KC not configured' },
        };
        const cfg = overall ? configs[overall] : null;
        if (!cfg) return <span className="inline-block h-2.5 w-2.5 rounded-full bg-gray-200" title="KC not checked" />;

        return (
            <span className="inline-flex items-center gap-1.5" title={`${cfg.label}${age !== null ? ` · ${age < 1 ? '<1 min ago' : `${age} min ago`}` : ''}`}>
                <span className={`inline-block h-2.5 w-2.5 rounded-full ${cfg.dot}`} />
                <span className="text-xs text-gray-500 dark:text-gray-400">{cfg.label}</span>
            </span>
        );
    };

    const getStatusBadgeColor = (status: string) => {
        switch (status) {
            case 'active':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
            case 'disabled':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
            case 'maintenance':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
            case 'deleted':
                return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
            default:
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{showDeleted ? 'Deleted Tenants' : 'Tenants'}</h1>
                <div className="mt-3 sm:mt-0 space-y-2 sm:space-y-0 sm:flex sm:space-x-3">
                    <button
                        onClick={() => setShowTestModal(true)}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                        <FiSearch className="mr-2" />
                        Test Tenant Provider
                    </button>

                    {!showDeleted && (
                        <>
                            <Link
                                to="/tenants/provision"
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                title="Provision a new tenant (Keycloak + MongoDB in one shot)"
                            >
                                <FiPlus className="mr-2" />
                                Provision Tenant
                            </Link>
                            <Link
                                to="/key-management"
                                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                title="Manage service authentication keys"
                            >
                                <FiKey className="mr-2" />
                                Key Management
                            </Link>
                        </>
                    )}
                    <button
                        onClick={() => setShowDeleted(!showDeleted)}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                        {showDeleted ? (
                            <>
                                <FiDatabase className="mr-2" />
                                Active Tenants
                            </>
                        ) : (
                            <>
                                <FiArchive className="mr-2" />
                                Deleted Tenants
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Search and Filter */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
                    <div className="relative flex-grow">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FiSearch className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-gray-900 dark:text-white sm:text-sm"
                            placeholder="Search tenants..."
                        />
                    </div>

                    {!showDeleted && (
                        <div className="flex-shrink-0">
                            <div className="relative inline-block">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FiFilter className="h-5 w-5 text-gray-400" />
                                </div>
                                <select
                                    id="statusFilter"
                                    name="statusFilter"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="block w-full pl-10 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md dark:bg-gray-700 dark:text-white"
                                >
                                    <option value="all">All Statuses</option>
                                    <option value="active">Active</option>
                                    <option value="disabled">Disabled</option>
                                    <option value="maintenance">Maintenance</option>
                                </select>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {error && (
                <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-800 rounded-md p-4 text-sm text-red-600 dark:text-red-300">
                    {error}
                </div>
            )}

            {/* Tenants table */}
            <div className="bg-white dark:bg-gray-800 shadow overflow-hidden rounded-lg">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th
                                    scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Tenant
                                </th>
                                <th
                                    scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Cluster
                                </th>
                                <th
                                    scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Status
                                </th>
                                <th
                                    scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Keycloak
                                </th>
                                <th
                                    scope="col"
                                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {loadingTenant ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-4 text-center">
                                        <div className="flex justify-center">
                                            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary-500"></div>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredTenants.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                                        {searchTerm || statusFilter !== 'all'
                                            ? 'No tenants match your search criteria'
                                            : showDeleted
                                                ? 'No deleted tenants found'
                                                : 'No tenants found. Create your first tenant to get started.'}
                                    </td>
                                </tr>
                            ) : (
                                filteredTenants.map((tenant) => (
                                    <tr key={tenant._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                        <td className="px-6 py-4">
                                            <div className="flex items-start">
                                                <FiDatabase className="flex-shrink-0 mr-2 mt-0.5 h-4 w-4 text-gray-400" />
                                                <div>
                                                    {/* Display Name — primary */}
                                                    <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                                        <Link
                                                            to={`/tenants/${tenant._id}`}
                                                            className="hover:text-primary-600 dark:hover:text-primary-400"
                                                        >
                                                            {tenant.name || tenant.tenantId}
                                                        </Link>
                                                    </div>
                                                    {/* Tenant name (slug from baseUrl) · tenantId */}
                                                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                                        {(tenant as any).baseUrl && (
                                                            <span className="text-xs text-blue-500 dark:text-blue-400 font-mono">
                                                                {(tenant as any).baseUrl.replace(/^https?:\/\//, '')}
                                                            </span>
                                                        )}
                                                        {(tenant as any).baseUrl && (
                                                            <span className="text-gray-400 text-xs">·</span>
                                                        )}
                                                        <span className="text-xs text-gray-400 dark:text-gray-500 font-mono">
                                                            {tenant.tenantId}
                                                        </span>
                                                    </div>
                                                    {tenant.dnsCreated && (
                                                        <span className="text-xs text-blue-600 dark:text-blue-400">DNS Active</span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                            {tenant.cluster}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${getStatusBadgeColor(tenant.status)}`}>
                                                {tenant.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {kcStatusDot(tenant)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex justify-end space-x-2">
                                                <Link
                                                    to={`/tenants/${tenant._id}`}
                                                    className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
                                                    title="View details"
                                                >
                                                    <FiEye className="h-5 w-5" />
                                                </Link>

                                                {!showDeleted && (
                                                    <>
                                                        <Link
                                                            to={`/key-management?tenantId=${tenant.tenantId}`}
                                                            className="text-yellow-600 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-300"
                                                            title="Manage keys"
                                                        >
                                                            <FiKey className="h-5 w-5" />
                                                        </Link>
                                                        <Link
                                                            to={`/tenants/${tenant._id}/edit`}
                                                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                                                            title="Edit tenant"
                                                        >
                                                            <FiEdit2 className="h-5 w-5" />
                                                        </Link>
                                                        <button
                                                            onClick={() => handleDuplicate(tenant._id)}
                                                            className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                                                            title="Duplicate tenant"
                                                        >
                                                            <FiCopy className="h-5 w-5" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(tenant._id, tenant.tenantId)}
                                                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                                            title="Delete tenant"
                                                        >
                                                            <FiTrash2 className="h-5 w-5" />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            {showTestModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
                    <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
                        <div className="p-6">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                                Test Tenant Provider Endpoint
                            </h3>

                            <div className="space-y-4">
                                <div>
                                    <label htmlFor="testDomain" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Tenant Domain
                                    </label>
                                    <input
                                        type="text"
                                        id="testDomain"
                                        value={testDomain}
                                        onChange={(e) => setTestDomain(e.target.value)}
                                        placeholder="e.g., dev.ontoris.com"
                                        className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                                    />
                                </div>
                               {testResult && (
                                    <div className={`p-4 rounded-md ${testResult.success ? 'bg-green-50 dark:bg-green-900' : 'bg-red-50 dark:bg-red-900'}`}>
                                        <h4 className={`text-sm font-medium mb-2 ${testResult.success ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'}`}>
                                            {testResult.success ? 'Success' : 'Error'}
                                        </h4>
                                        <pre className={`text-xs overflow-auto ${testResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                                            {testResult.success ? testResult.data.tenantId : JSON.stringify(testResult.error, null, 2)}
                                        </pre>
                                    </div>
                                )}
                            </div>

                            <div className="mt-6 flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowTestModal(false);
                                        setTestDomain('');
                                        setTestApiKey('');
                                        setTestResult(null);
                                    }}
                                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                >
                                    Close
                                </button>
                                <button
                                    type="button"
                                    onClick={handleTestProvider}
                                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                >
                                    Test Endpoint
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TenantsPage;